/* eslint-disable @typescript-eslint/no-explicit-any */
import type * as React from "react";
import { type FC, type ReactNode, useMemo } from "react";
import { type TextFieldVariants } from "~/common/types";
import { MenuItem, TextField } from "~/common/mui";

export interface MultilevelSelectItemItem {
  label: string;
  value: string;
  title?: boolean; // if item is title, then is not selectable/clickable
  parentValue?: string;
  children?: MultilevelSelectItemItem[];
}

interface MultilevelSelectProps {
  list: MultilevelSelectItemItem[];
  label?: ReactNode;
  value?: any;
  onChange?: (option: any) => void;
  onBlur?: () => void;
  "data-test-id"?: string;
  helperLabel?: ReactNode;
  disabled?: boolean;
  disabledLabel?: boolean;
  variant?: TextFieldVariants;
  highlight?: boolean;
  error?: boolean;
  size?: "small" | "medium";
}

export const MultilevelSelect: FC<MultilevelSelectProps> = ({
  list,
  label,
  "data-test-id": testID,
  disabled,
  disabledLabel,
  variant,
  highlight,
  helperLabel,
  ...rest
}) => {
  const elements = useMemo(() => createItems(list), [list]);

  const elementMap = useMemo(() => {
    const map: Dict<MultilevelSelectItemItem> = {};
    list.forEach((item) => {
      item.children?.forEach((child) => {
        map[child.value] = child;
      });
      map[item.value] = item;
    });
    return map;
  }, [list]);

  return (
    <div className={"scha__selection__multiple-levels"}>
      <TextField
        select
        label={label}
        slotProps={{
          select: {
            renderValue: (option) => renderOption(option, elementMap, disabledLabel),
          },
        }}
        helperText={helperLabel}
        fullWidth={true}
        disabled={disabled}
        variant={variant}
        highlight={highlight}
        {...rest}
      >
        {elements}
      </TextField>
    </div>
  );
};

const createItems = (list: MultilevelSelectItemItem[], level: number = 1): ReactNode[] => {
  return list.map((item, index) => {
    const isTitle = item.title || false;

    let subList;
    let hasSublist = false;
    if (item.children && item.children.length > 0) {
      hasSublist = true;
      subList = createItems(item.children, level + 1);
    }

    const itemContent = isTitle || (isTitle && hasSublist) ? itemTitle(level, item) : itemOption(level, item);

    return [itemContent, subList];
  });
};

const itemTitle = (level: number, data: MultilevelSelectItemItem): ReactNode => (
  <MenuItem className={"option title level-" + level} style={calcStyles(level)} disabled>
    {data.label}
  </MenuItem>
);

const itemOption = (level: number, data: MultilevelSelectItemItem): ReactNode => (
  <MenuItem className={"option level-" + level} style={calcStyles(level)} value={data.value}>
    {data.label}
  </MenuItem>
);

const indentLeftSize: number = 15; // TODO change to 16 with checkbox change
const calcStyles = (level: number) => {
  return { paddingLeft: level * indentLeftSize + "px" };
};

const renderOption = (
  value: any /* but actually string here */,
  map: Dict<MultilevelSelectItemItem>,
  disabledLabel?: boolean,
): React.ReactNode => {
  const option = map[value];

  if (!option) {
    return <span>-</span>;
  }

  const optionTitle: string[] = [];
  optionTitle.push(option.label);

  if (option.parentValue && !disabledLabel) {
    const parent = map[option.parentValue];
    optionTitle.push(parent.label);
  }

  return <span>{optionTitle.join(" - ")}</span>;
};
